const CELL = 2
const BAR_HEIGHT = 4
const POOL = 256

/** Which cells of a column a tick lights. 1 = top, 2 = bottom, 3 = both. */
type TickMask = 1 | 2 | 3

/** Separated ticks. 0 = a gap. */
const SPARSE_MASKS: ReadonlyArray<TickMask | 0> = [3, 1, 0, 3, 2, 0, 1, 0, 3, 2, 0, 3, 1, 0, 2, 0]
const DENSE_MASKS: ReadonlyArray<TickMask | 0> = [3, 3, 1, 3, 3, 2, 3, 1, 3, 3, 2, 3, 3, 1]

interface Tick {
  x: number
  mask: TickMask
  alive: boolean
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value))
}

/**
 * Pixel progress bar. 2×2 blocks spawn off the right edge, slide left at a constant speed,
 * and lock into a solid stack. No per-frame allocation.
 */
export class BlockStream {
  private readonly canvas: HTMLCanvasElement
  private ctx: CanvasRenderingContext2D | null = null
  private readonly ticks: Array<Tick> = Array.from({ length: POOL }, () => ({
    x: 0,
    mask: 1,
    alive: false,
  }))

  private cssWidth = 0
  /** Two cells per 2px column. */
  private totalCells = 0
  private scale = 1
  private value = 0
  private timespan = 1000
  /** Whole cells locked into the fill. `easeTo` is fractional during auto-fill; this never is. */
  private settledCells = 0
  private credit = 0
  /** Cells the current percentage owns. Auto-fill never counts as landed before this. */
  private committed = 0
  private easeTo = 0
  private patternIndex = 0
  private lastFrame = 0
  /** How long the current wave takes to cross the bar. Larger jumps are faster. */
  private rushSeconds = 1
  private reduceMotion = false
  private color = 'currentColor'

  constructor(canvas: HTMLCanvasElement) {
    this.canvas = canvas
    this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
  }

  resize(cssWidth: number): void {
    const width = Math.max(0, Math.floor(cssWidth))
    const scale = Math.max(1, Math.round(window.devicePixelRatio || 1))
    if (width === this.cssWidth && scale === this.scale && this.ctx) {
      return
    }

    const ratio = this.fraction()
    this.cssWidth = width
    this.totalCells = Math.floor(width / CELL) * 2
    this.scale = scale
    this.canvas.width = Math.max(1, width * scale)
    this.canvas.height = BAR_HEIGHT * scale
    const ctx = this.canvas.getContext('2d', { alpha: true })
    this.ctx = ctx
    if (ctx) {
      ctx.imageSmoothingEnabled = false
    }

    this.settledCells = Math.round(ratio * this.totalCells)
    this.credit = 0
    this.killAll()
    this.retarget()
    // retarget clamps to the real value; put back any auto-fill that had crept past it.
    this.restore(ratio)
  }

  setColor(color: string): void {
    this.color = color
  }

  /** Fill fraction on screen, auto-fill included. */
  fraction(): number {
    return this.totalCells > 0 ? this.settledCells / this.totalCells : 0
  }

  /** Shows `fraction` as already landed, so a remounted bar picks up where the last one left off. */
  restore(fraction: number): void {
    const cells = Math.round(clamp(fraction, 0, 1) * this.totalCells)
    if (cells <= this.settledCells) {
      return
    }
    this.settledCells = cells
    this.easeTo = Math.max(this.easeTo, cells)
    this.credit = 0
    this.killAll()
  }

  setInputs(value: number, timespan: number): void {
    this.timespan = Math.max(32, timespan)
    const next = clamp(value, 0, 100)
    if (next === this.value) {
      return
    }
    this.value = next
    this.retarget()
  }

  frame(now: number): void {
    if (!this.ctx || this.cssWidth <= 0) {
      return
    }

    if (this.reduceMotion) {
      this.settledCells = Math.floor(this.easeTo)
      this.credit = 0
      this.killAll()
      this.draw(this.ctx)
      return
    }

    const elapsed = this.lastFrame === 0 ? 0 : Math.min(0.05, (now - this.lastFrame) / 1000)
    this.lastFrame = now

    if (!this.catchingUp) {
      this.creep(elapsed)
    }
    const deficit = this.easeTo - this.settledCells - this.countInFlight() - this.credit
    if (deficit > 0) {
      this.credit += deficit
    }

    this.spawn()
    this.advance(elapsed)
    this.draw(this.ctx)
  }

  /** A real step is still landing, as opposed to auto-fill. */
  private get catchingUp(): boolean {
    return this.settledCells < this.committed - 0.5
  }

  private retarget(): void {
    if (this.totalCells <= 0) {
      return
    }

    const target = Math.round((this.value / 100) * this.totalCells)
    if (target <= 0) {
      this.settledCells = 0
      this.credit = 0
      this.committed = 0
      this.easeTo = 0
      this.killAll()
      return
    }
    if (target < this.settledCells) {
      this.settledCells = target
    }

    const jump = Math.max(0, target - this.settledCells)
    const volume = jump / this.totalCells
    // A real step crosses on its own clock. Bigger jumps are a bit longer so the stream stays readable.
    this.rushSeconds = jump > 0 ? 0.64 + volume * 0.24 : this.rushSeconds

    // Blocks already streaming from auto-fill join the step: they speed up to the step clock, and only the
    // remainder spawns off-screen on the right.
    this.committed = target
    this.easeTo = target
  }

  /**
   * Keep feeding a few cells for the timespan after a percentage lands, and
   * from 0% before the first one: a fresh account sits at 0 until its first
   * integration finishes, and an empty, still bar reads as broken.
   */
  private creep(dt: number): void {
    if (dt <= 0 || this.value >= 100) {
      return
    }

    const headroom = Math.max(6, (100 - this.value) * 0.18)
    const ceilingValue = Math.min(97, this.value + headroom)
    const ceiling = Math.min(this.totalCells, Math.round((ceilingValue / 100) * this.totalCells))
    if (this.easeTo >= ceiling) {
      return
    }

    const tau = this.timespan / 1000
    this.easeTo += (ceiling - this.easeTo) * (1 - Math.exp(-dt / tau))
  }

  private spawn(): void {
    const edge = Math.floor(this.settledCells / 2) * CELL
    if (this.credit < 1) {
      return
    }
    if (this.cssWidth - edge < CELL) {
      this.settledCells = Math.floor(Math.min(this.easeTo, this.settledCells + this.credit))
      this.credit = 0
      return
    }

    const dense = this.credit > 6
    const masks = dense ? DENSE_MASKS : SPARSE_MASKS
    const gap = dense ? CELL * 2 : CELL * 4
    let rightmost = this.cssWidth - gap
    for (const tick of this.ticks) {
      if (tick.alive && tick.x > rightmost) {
        rightmost = tick.x
      }
    }

    let guard = 0
    const burst = this.credit > 6 ? 64 : 8
    while (this.credit >= 1 && guard < burst) {
      guard += 1
      const mask = masks[this.patternIndex % masks.length]
      this.patternIndex += 1
      if (mask === 0) {
        rightmost += gap
        continue
      }

      const tick = this.acquire()
      if (!tick) {
        return
      }

      const cost = mask === 3 && this.credit >= 2 ? 2 : 1
      rightmost += gap
      tick.x = Math.max(this.cssWidth, rightmost)
      rightmost = tick.x
      tick.mask = cost === 2 ? 3 : mask === 3 ? 1 : mask
      tick.alive = true
      this.credit -= cost
    }
  }

  private advance(dt: number): void {
    const edge = Math.floor(this.settledCells / 2) * CELL
    if (dt > 0) {
      const span = Math.max(CELL * 8, this.cssWidth - edge)
      const travel = this.catchingUp ? this.rushSeconds : Math.max(0.45, this.timespan / 1000)
      const speed = span / travel
      for (const tick of this.ticks) {
        if (!tick.alive) {
          continue
        }
        tick.x -= speed * dt
      }
    }

    let guard = 0
    const absorbCap = this.catchingUp ? 64 : 8
    while (this.settledCells < this.easeTo && guard < absorbCap) {
      guard += 1
      const edgeNow = Math.floor(this.settledCells / 2) * CELL
      const tick = this.touching(edgeNow)
      if (!tick) {
        break
      }
      const cost = tick.mask === 3 ? 2 : 1
      if (this.settledCells + cost > this.easeTo) {
        break
      }
      this.settledCells += cost
      tick.alive = false
    }

    // Surplus ticks (after a decrease) slide under the fill, where draw already clips them to nothing.
    const hiddenEdge = Math.floor(this.settledCells / 2) * CELL - CELL
    for (const tick of this.ticks) {
      if (tick.alive && tick.x <= hiddenEdge) {
        tick.alive = false
      }
    }
  }

  private touching(edge: number): Tick | null {
    let found: Tick | null = null
    for (const tick of this.ticks) {
      if (!tick.alive || tick.x > edge) {
        continue
      }
      if (!found || tick.x < found.x) {
        found = tick
      }
    }
    return found
  }

  private draw(ctx: CanvasRenderingContext2D): void {
    const scale = this.scale
    const cell = CELL * scale
    ctx.clearRect(0, 0, this.cssWidth * scale, BAR_HEIGHT * scale)
    ctx.fillStyle = this.color

    const solidColumns = Math.floor(this.settledCells / 2)
    if (solidColumns > 0) {
      ctx.fillRect(0, 0, solidColumns * cell, cell * 2)
    }
    if (this.settledCells % 2 === 1) {
      ctx.fillRect(solidColumns * cell, cell, cell, cell)
    }

    for (const tick of this.ticks) {
      if (!tick.alive) {
        continue
      }
      if (tick.x >= this.cssWidth || tick.x + CELL <= 0) {
        continue
      }
      const left = Math.max(solidColumns * CELL, tick.x)
      const right = Math.min(this.cssWidth, tick.x + CELL)
      if (right <= left) {
        continue
      }
      const x = left * scale
      const w = (right - left) * scale
      if (tick.mask & 1) {
        ctx.fillRect(x, 0, w, cell)
      }
      if (tick.mask & 2) {
        ctx.fillRect(x, cell, w, cell)
      }
    }
  }

  private acquire(): Tick | null {
    for (const tick of this.ticks) {
      if (!tick.alive) {
        return tick
      }
    }
    return null
  }

  private countInFlight(): number {
    let cells = 0
    for (const tick of this.ticks) {
      if (!tick.alive) {
        continue
      }
      cells += tick.mask === 3 ? 2 : 1
    }
    return cells
  }

  private killAll(): void {
    for (const tick of this.ticks) {
      tick.alive = false
    }
  }
}
